import React, { useEffect, useState } from 'react'
import { Radio, FormControlLabel } from '@material-ui/core'
import { Input } from './../Form/Input'
import { ButtonDelete } from './../Buttons/ButtonDelete'
import './types'
import {
  convertMonetaryDollarStringToCentValue,
  convertCentValueToMonetaryString,
} from './../../Utils/Formatters'
import { number } from '../../Utils/Validators/formValidation'
import { SelectDashboard } from '../Form'
import { useDispatch } from 'react-redux'
import { showReduxModal } from '../../Reducers/UI/actions'
import { InputV2 } from '../Form/InputV2'

/**
 * @param {MetricRowProps} props
 */
const MetricRow = props => {
  const {
    allMetrics,
    defaultMetrics,
    id,
    name,
    label,
    targetConversionRate,
    targetCostPerMetric,
    onTargetCostPerMetricChange,
    onTargetConversionRateChange,
    onStandartMetricChange,
    onNameChange,
    canMetricEdit,
    setCanMetricEdit,
  } = props
  const [inputTargetConversionRate, setInputTargetConversionRate] = useState(
    convertConversionRateValueToPercentString(targetConversionRate),
  )
  const [inputTargetCostPerMetric, setInputTargetCostPerMetric] = useState(
    convertCentValueToMonetaryString(targetCostPerMetric),
  )
  const [standartMetricsList, setStandartMetricsList] = useState([])
  const [errors, setErrors] = useState(null)

  const dispatch = useDispatch()

  useEffect(() => {
    validateLabel(label)
  }, [name])

  useEffect(() => {
    getStandartMetricsList(defaultMetrics)
  }, [defaultMetrics])

  const getStandartMetricsList = defaultMetrics => {
    const convertedOptions = defaultMetrics.map(metric => ({
      label: metric.value,
      value: metric.value,
    }))
    let defaultValues = [...convertedOptions]

    if (allMetrics.length == 0) {
      setStandartMetricsList([
        ...defaultValues,
        { label: 'Other', value: 'Other' },
      ])
    }

    const existingStandartMetrics = Object.keys(
      allMetrics.reduce((acc, metric) => {
        if (metric.name == name) return acc
        acc[metric.name] = metric
        return acc
      }, {}),
    )
    const defaultValuesWithoutAlreadySelected = defaultValues.filter(
      item => !existingStandartMetrics.includes(item.value),
    )
    setStandartMetricsList([
      ...defaultValuesWithoutAlreadySelected,
      { label: 'Other', value: 'Other' },
    ])
  }
  const validateLabel = label => {
    if (!label && name == 'Other') {
      return setErrors({ ...errors, metricLabelError: 'Required' })
    } else {
      return setErrors({ ...errors, metricLabelError: null })
    }
  }
  return (
    <div>
      <div className="metrics-row">
        <div
          className="section-column"
          onClick={() => {
            if (canMetricEdit || id.includes('new')) return
            return dispatch(
              showReduxModal({
                title: 'Warning',
                children:
                  'Changing the metric for an active campaign may cause problems with conversions and the campaign dashboard. Do you really want to change the metric?',
                onConfirm: () => {
                  setCanMetricEdit(true)
                },
              }),
            )
          }}
        >
          <SelectDashboard
            key={`name-${name}`}
            label={'Metric'}
            disabled={!canMetricEdit && !id.includes('new')}
            onChange={(a, b, value) => {
              onNameChange(id, value)
            }}
            options={standartMetricsList}
            value={name}
            error={null}
            cypress_id="create-campaign-metrics_type-dropdown"
          />
        </div>

        <div className="section-column">
          <InputV2
            label={name === 'Other' ? 'Metric name' : 'Metric name (optional)'}
            hint={
              name === 'Other'
                ? 'Metric name is what will be displayed to the client on the campaign dashboards etc. If you have selected a standard metric from the dropdown, then metric name is optional. If you have selected ‘Other’ as a metric, then entering metric name is mandatory.'
                : null
            }
            disabled={!name}
            error={errors ? errors.metricLabelError : null}
            placeholder="Metric's Name"
            value={label}
            onChange={e => {
              onStandartMetricChange(id, e.target.value)
            }}
            onBlur={e => {
              validateLabel(e.target.value)
            }}
            cypress_id="create-campaign-metrics_name-input"
          />
        </div>
        <div className="section-column">
          <Input
            type="number"
            label="Target Conversion Rate"
            name="targetConversionRate"
            placeholder="Estimate"
            value={inputTargetConversionRate}
            className="input-with-icon input-with-icon_percent"
            onChange={e => setInputTargetConversionRate(e.target.value)}
            onBlur={e => {
              var newRate = convertConversionRatePercentStringToValue(
                e.target.value,
              )
              if (newRate === null) setInputTargetConversionRate('')
              onTargetConversionRateChange(id, newRate)
            }}
            validator={[number]}
            cypress_id="create-campaign-target_conversion_rate-input"
            content="Can be added if the target is provided by the client."
          />
        </div>
        <div className="section-column">
          <Input
            type="number"
            label="Target Cost Per Metric"
            name="targetCostPerMetric"
            placeholder="Estimate"
            value={inputTargetCostPerMetric}
            onChange={e => setInputTargetCostPerMetric(e.target.value)}
            onBlur={e => {
              const newVal = convertMonetaryDollarStringToCentValue(
                e.target.value,
              )
              if (newVal === null) setInputTargetCostPerMetric('')
              onTargetCostPerMetricChange(id, newVal)
            }}
            validator={[number]}
            cypress_id="create-campaign-target_cost_per_metric-input"
            content="Can be added if the target is provided by the client."
          />
        </div>
        <div className="section-checkbox-column">
          <FormControlLabel
            control={
              <Radio
                label="Primary Metric"
                checked={props.isPrimary}
                onClick={props.onPrimaryMetricToggle}
                cypress_id="create-campaign-primary_metric-radio"
                color="primary"
              />
            }
            label="Primary Metric"
          />
        </div>
        <div className="section-button-column">
          <ButtonDelete onClickHandler={() => props.onDelete(id)} />
        </div>
      </div>
    </div>
  )
}

export default MetricRow

/**
 * Convert the visual percent value to a numeric decimal value.
 * @param {string} str
 * @returns {number|null}
 */
function convertConversionRatePercentStringToValue(str) {
  if (str === '') return null
  var val = Number(str)
  if (isNaN(val)) return null
  return val / 100
}

/**
 * Convert the real conversion rate value to a string percent value.
 * @param {number} val
 * @returns {string}
 */
function convertConversionRateValueToPercentString(val) {
  if (val == null || val == undefined) return null
  return val * 100 + ''
}
